import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useNavigate } from 'react-router-dom';
import { Background, Panel, ReactFlow, useReactFlow, useOnViewportChange } from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useHotkeyScope } from '@/hooks/useHotkeyScope';
import { useMousePosition } from '@/hooks/useMousePosition';
import { useKeyNavigation } from '@/hooks/useKeyNavigation';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { log } from '@/logger/logger.ts';
import { isFileTypeAccepted } from '@/utils/fileTypes';
import { FF_NODE_GROUPING } from '@/consts/featureFlags';
import { useQueryParamsContext } from '@/contexts/QueryParamsContext';
import { color } from '@/colors';
import { RecipeType } from '@/enums/recipe-type.enum';
import { NodeType } from '@/enums/node-type.enum';
import { FlowMode } from '@/enums/flow-modes.enum';
import { I18N_KEYS } from '@/language/keys';
import { EyeIcon } from '@/UI/Icons/EyeIcon';
import { WarningCircleIcon } from '@/UI/Icons/WarningCircleIcon';
import { AppPaper, Flex } from '@/UI/styles';
import { AppToggleButtons } from '@/UI/AppToggleButtons/AppToggleButtons';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { useWorkflowStore, useUserWorkflowRole, useResetWorkflowState } from '@/state/workflow.state';
import { useGlobalStore } from '@/state/global.state';
import { useProximityConnect } from '@/hooks/useProximityConnect';
import { getShortcut } from '@/utils/shortcuts';
import { useSaveRecipe } from '@/hooks/useSaveRecipe';
import { useDeleteEdgeOnSwipe } from '@/hooks/useDeleteEdgeOnSwipe';
import { goToRecipe } from '@/utils/recipes';
import { ZIndex } from '@/consts/z-indexes.consts';
import { PROMPT_NODE_ID } from '@/consts/node-types.consts';
import { getDashboardSectionFromScope } from '@/components/Dashboard/utils/dashboardSection.utils';
import { useDuplicateRecipe } from '@/queries/useRecipes';
import { useNodeActions } from '../Nodes/useNodeActions';
import * as Nodes from '../Nodes';
import DeleteGroupDialog from '../Nodes/DeleteGroupDialog';
import { ShortcutsPanel } from '../ShortcutsPanel/ShortcutsPanel';
import { FlowActionsBar } from './FlowComponents/FlowActionsBar';
import LeftToolMenuV2 from './FlowComponents/LeftToolMenuV2';
import PropertiesDrawerV2 from './Drawer/PropertiesDrawerV2';
import { Editor } from './FlowComponents/Editor/Editor';
import ModelMediaGallery from './FlowComponents/ModelMediaGallery';
import { FlowTour } from './FlowComponents/FlowTourElements';
import { useMediaGalleryContext } from './FlowComponents/MediaGalleryContext';
import { CustomEdge } from './CustomEdge';
import CustomTempEdge from './CustomTempEdge';
import DesignApp from './DesignApp/DesignApp';
import { useFlowNavigationTour } from './FlowComponents/FlowTour/useFlowNavigationTour';
import { TargetNode } from './FlowComponents/FlowTour/TargetNode';
import { useGraph } from './useGraph';
import { useFlowView, useGroupsManagerView, useStackedNodesAndEdgesForRendering } from './FlowContext';
import { useReactFlowProps } from './hooks/useReactFlowProps';
import { usePropertiesDrawer } from './hooks/usePropertiesDrawer';
import { isRecipeInteractionAllowed, toReactFlowNode } from './utils';
import { NotificationsPanel } from './FlowComponents/NotificationsPanel';
import { CreditsInfo } from './FlowComponents/CreditsInfo/CreditsInfo';
import { WorkflowDesignAppSwitch } from './FlowComponents/WorkflowDesignAppSwitch/WorkflowDesignAppSwitch';
import { useEditorStore } from './FlowComponents/Editor/editor.state';
import { CREDITS_INFO_WIDTH, PANEL_WIDTH, TOOLBAR_WIDTH_V2 } from './consts/ui';
import { wrapNode } from './NodeWrapper';
import LeftPanelV2 from './FlowComponents/LeftPanelV2';
import { SuggestionMenuTooltip } from './FlowComponents/SuggestionMenuTooltip';
import ShareWorkflowModal from './FlowComponents/ShareWorkflowModal';
import { useUserPreferences } from './hooks/useUserPreferences';
import TaskManager from './RunFlow/TaskManager/TaskManager';
import { useBatchesByRecipeId, useBatchesStore } from './RunFlow/batches.store';
import { useFlowRunContext } from './RunFlow/FlowRunContext';
import { isSomeNodesInRunningBatch } from './RunFlow/batch.utils';
import { useAutoSaveRecipe } from './hooks/useAutoSaveRecipe';
import SaveErrorSnackbar from './FlowComponents/SaveErrorSnackbar';
import { useContextMenus } from './hooks/useContextMenus';

const MIN_ZOOM_LEVEL = 0.01;
const MAX_ZOOM_LEVEL = 3;

const logger = log.getLogger('Flow');

const NAVBAR_HEIGHT = 48;
const READ_ONLY_PANEL_HEIGHT = 40;
const READ_ONLY_PANEL_EDITOR_WIDTH = 400;
const READ_ONLY_PANEL_GUEST_WIDTH = 480;

const DESIGN_APP_TOP_HEIGHT = 56;

const BLUR_AMOUNT = 3;

function Flow({ user, recipeId, viewingVersionMode }) {
  const recipe = useWorkflowStore((state) => state.recipe);
  const resetWorkflowState = useResetWorkflowState();

  const isNodeGroupingEnabled = useFeatureFlagEnabled(FF_NODE_GROUPING);

  const isDesignApp = recipe?.type === RecipeType.DesignApp; // temp
  const modelPricesMaps = useWorkflowStore((state) => state.modelPricesMaps);
  const isEditingName = useWorkflowStore((state) => state.isEditingNameOnWorkflow);
  const setIsEditingName = useWorkflowStore((state) => state.setIsEditingNameOnWorkflow);
  const flowView = useFlowView();
  const groupsManagerView = useGroupsManagerView();
  const { nodes, edges } = useStackedNodesAndEdgesForRendering();

  const saveRecipe = useSaveRecipe();
  useAutoSaveRecipe();
  const { mutateAsync: duplicateRecipe } = useDuplicateRecipe();

  const selectedNodes = useMemo(() => nodes.filter((n) => n.selected), [nodes]);
  const { nodesForPropertiesDrawer } = usePropertiesDrawer(selectedNodes);

  const { queryParamViewingMode } = useQueryParamsContext();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { t } = useTranslation();
  // preferences
  const {
    userPrefOnScroll,
    userPrefShowFloatMenuOnRightClick,
    userPrefRequireAltKeyForSuggestions,
    userPrefEdgeType,
    userPrefSnapToGrid,
    handlePanOnScrollChange,
    handleShowFloatMenuOnRightClickChange,
    handleRequireAltKeyForSuggestionsChange,
    handleEdgeTypeChange,
    handleSnapToGridChange,
  } = useUserPreferences();
  // end of preferences

  const navigate = useNavigate();
  const role = useUserWorkflowRole();

  const [isSelectMode, setIsSelectMode] = useState(true);
  const [reactFlowInitialized, setReactFlowInitialized] = useState(false);

  const [notificationData, setNotificationData] = useState({
    isOpen: false,
  });

  useOnViewportChange({
    onEnd: () => {
      flowView.redraw();
    },
  });

  // Container size tracking
  const flowRef = useRef(null);
  const setReactFlowContainerRect = useWorkflowStore((s) => s.setReactFlowContainerRect);
  useEffect(() => {
    const updateContainerSize = () => {
      const reactFlowContainer = flowRef.current;
      if (reactFlowContainer) {
        const rect = reactFlowContainer.getBoundingClientRect();
        setReactFlowContainerRect(rect);
      }
    };
    // Initial size measurement
    updateContainerSize();

    // Set up ResizeObserver
    const reactFlowContainer = flowRef.current;
    if (reactFlowContainer) {
      const observer = new ResizeObserver(updateContainerSize);
      observer.observe(reactFlowContainer);
      return () => observer.disconnect();
    }
  }, [setReactFlowContainerRect]);

  const reactFlowInstanceRef = useRef(null);

  const onInit = useCallback((instance) => {
    reactFlowInstanceRef.current = instance;
    setReactFlowInitialized(true);
  }, []);

  const {
    addNewNode: graphAddNewNode,
    copyNodesToClipboard,
    pasteNodesOrImageFromClipboard: graphPasteNodesOrImageFromClipboard,
  } = useGraph();

  const addNewNode = useCallback(
    (nodeOptions) => {
      if (role !== 'editor') return; //todo: centralize the permissions

      graphAddNewNode(nodeOptions);
      void saveRecipe();
    },
    [graphAddNewNode, role, saveRecipe],
  );

  const {
    handleNodeContextMenu,
    handleSelectionContextMenu,
    isNodeMenuOpened,
    menuComponents,
    floatMenu: {
      openFloatMenu,
      handleFloatMenu,
      menu: { isOpen: isFloatMenuOpen },
    },
    suggestionMenu: {
      onConnect: onSuggestionConnect,
      onEdgeUpdateStart: onSuggestionEdgeUpdateStart,
      onConnectStart: onSuggestionConnectStart,
      onConnectEnd: onSuggestionConnectEnd,
      anchorEl: suggestionMenuAnchorEl,
      isTooltipOpen: suggestionMenuIsTooltipOpen,
    },
  } = useContextMenus({ addNewNode });

  // Track mouse position for keyboard shortcuts
  const mousePosition = useMousePosition();

  /// GALLERY
  const { isGalleryOpen } = useMediaGalleryContext();
  /// EDITOR
  const isEditorOpen = useEditorStore((selector) => selector.isEditorOpen);
  const clearEditor = useEditorStore((selector) => selector.clearNodeId);
  const openEditor = useEditorStore((selector) => selector.openEditor);

  const [viewport, setViewport] = useState(null);
  const handleOpenEditor = useCallback(
    (nodeId) => {
      setViewport(reactFlowInstanceRef.current.getViewport());
      openEditor(nodeId);
    },
    [openEditor],
  );

  /// main toolbar
  const [selectedMenu, setSelectedMenu] = useState();

  /// DIALOG
  const dialogData = useGlobalStore((s) => s.dialogData);
  const deleteGroupDialogData = dialogData?.type === 'deleteGroup' ? dialogData : undefined;
  const closeOverlayDialog = useGlobalStore((s) => s.closeDialog);

  const DEFAULT_ZOOM_LEVEL = nodes.length > 1 ? 0.05 : 0.5;
  const { fitView } = useReactFlow();

  /// FLOW VIEWING MODE
  const shouldShowDesignAppToggle = useMemo(() => {
    if (!nodes?.some((node) => node.type === NodeType.WorkflowOutput)) return false;
    if (role === 'editor') return true;
    return !!recipe.latestPublishedVersion;
  }, [nodes, role, recipe.latestPublishedVersion]);

  const [flowViewingMode, setFlowViewingMode] = useState(() => {
    const viewParam = queryParamViewingMode;

    if (role === 'editor' && viewParam === FlowMode.App && shouldShowDesignAppToggle) {
      return FlowMode.App;
    }

    if (role !== 'editor' && !recipe.latestPublishedVersion) {
      // prevent from guest to see the app mode if no published version
      return FlowMode.Workflow;
    }

    if (viewParam) {
      if (viewParam === FlowMode.App && !shouldShowDesignAppToggle) {
        return FlowMode.Workflow;
      }
      return viewParam;
    }
    return role === 'editor' || !shouldShowDesignAppToggle ? FlowMode.Workflow : FlowMode.App;
  });

  /**
   * @deprecated Update node data through "View"s and FlowGraph instead.
   */
  const updateNodeData = useCallback((id, data) => flowView.updateNodeData(id, data), [flowView]);
  const { duplicateNodes: duplicateNodesAction, deleteNode, deleteNodes, deleteSelected } = useNodeActions();

  const handleDeleteGroupConfirm = useCallback(() => {
    if (!deleteGroupDialogData) return;
    deleteNodes(deleteGroupDialogData.allNodeIdsToDelete, true);
  }, [deleteGroupDialogData, deleteNodes]);

  const duplicateNodes = useCallback(() => {
    if (role !== 'editor') return;
    duplicateNodesAction(flowView.selectedNodes);
  }, [flowView, duplicateNodesAction, role]);
  const pasteNodesOrImageFromClipboard = useCallback(
    (event) => {
      graphPasteNodesOrImageFromClipboard(event);
      void saveRecipe();
    },
    [graphPasteNodesOrImageFromClipboard, saveRecipe],
  );
  const openToolboxDrawer = useCallback(() => {
    setSelectedMenu('image');
  }, []);

  const closeToolboxDrawer = useCallback(() => {
    setSelectedMenu(null);
  }, []);

  const { activeTour } = useFlowNavigationTour({
    addNewNode,
    closeToolboxDrawer,
    deleteNode,
    openToolboxDrawer,
    reactFlowInitialized,
    recipeId,
    updateNodeData,
  });

  const { fetchMenuItems } = useFetchMenuItems();

  // Hook for deleting edges on Cmd/Ctrl drag
  const { getDragState } = useDeleteEdgeOnSwipe();

  useEffect(() => {
    if (role !== 'editor') return;

    fetchMenuItems();
  }, [fetchMenuItems, role]);

  //// RUNNING MODELS

  const isTaskManagerOpen = useBatchesStore((s) => s.isTaskManagerOpen);
  const batches = useBatchesByRecipeId(recipeId);

  const staticNodeTypesComponent = React.useMemo(
    () => ({
      array: wrapNode((nodeData) => <Nodes.ArrayNode {...nodeData} />),
      blur: wrapNode((nodeData) => <Nodes.BlurNode {...nodeData} />),
      boolean: wrapNode((nodeData) => <Nodes.ToggleNode {...nodeData} />),
      channels: wrapNode((nodeData) => <Nodes.ChannelsNode {...nodeData} />),
      deprecated: wrapNode((nodeData) => <Nodes.DeprecatedNode {...nodeData} />),
      compv3: wrapNode((nodeData) => <Nodes.CompNodeV3 {...nodeData} openEditWindow={handleOpenEditor} />),
      comparison: wrapNode((nodeData) => <Nodes.ComparisonNode {...nodeData} />),
      crop: wrapNode((nodeData) => <Nodes.CropNode {...nodeData} />),
      [NodeType.CustomGroup]: wrapNode((nodeData) => <Nodes.CustomGroupNode {...nodeData} />),
      custommodelV2: wrapNode((nodeData) => (
        <Nodes.ModelBaseNode
          editable={false}
          modelPricesMaps={modelPricesMaps}
          updateNodeData={updateNodeData}
          {...nodeData}
        />
      )),
      export: wrapNode((nodeData) => <Nodes.ExportNode {...nodeData} />),
      figma_compositor: wrapNode((nodeData) => <Nodes.FigmaCompositorNode {...nodeData} />),
      extract_video_frame: wrapNode((nodeData) => <Nodes.ExtractVideoFrameNode {...nodeData} />),
      import: wrapNode((nodeData) => <Nodes.ImportNode {...nodeData} updateNodeData={updateNodeData} />),
      kling_element: wrapNode((nodeData) => <Nodes.KlingElementNode {...nodeData} />),
      media_iterator: wrapNode((nodeData) => <Nodes.MediaIteratorNode {...nodeData} />),
      integer: wrapNode((nodeData) => <Nodes.NumberNode {...nodeData} />),
      invert: wrapNode((nodeData) => <Nodes.InvertNode {...nodeData} />),
      levels: wrapNode((nodeData) => <Nodes.LevelsNode {...nodeData} />),
      dilation_erosion: wrapNode((nodeData) => <Nodes.DilationErosionNode {...nodeData} />),
      masks: wrapNode((nodeData) => <Nodes.MaskExtractorNode {...nodeData} />),
      merge_alpha: wrapNode((nodeData) => <Nodes.MergeAlphaNode {...nodeData} />),
      multilora: wrapNode((nodeData) =>
        nodeData.data.viewMode === NodeViewMode.Single ? (
          <Nodes.LoraNode {...nodeData} />
        ) : (
          <Nodes.ImportLoraNode {...nodeData} />
        ),
      ),
      muxv2: wrapNode((nodeData) => <Nodes.ListSelectorNode {...nodeData} />),
      number_selector: wrapNode((nodeData) => <Nodes.NumberSelectorNode {...nodeData} />),
      painterV2: wrapNode((nodeData) => <Nodes.PainterNode {...nodeData} />),
      preview: wrapNode((nodeData) => <Nodes.PreviewNodeV2 {...nodeData} />),
      promptV3: wrapNode((nodeData) => <Nodes.PromptNodeV3 {...nodeData} addNewNode={addNewNode} />),
      prompt_concat: wrapNode((nodeData) => <Nodes.PromptConcat {...nodeData} />),
      resize: wrapNode((nodeData) => <Nodes.ResizeNode {...nodeData} />),
      router: wrapNode((nodeData) => <Nodes.RouterNode {...nodeData} />),
      seed: wrapNode((nodeData) => <Nodes.SeedNode {...nodeData} />),
      stickynote: wrapNode((nodeData) => <Nodes.StickyNoteNode {...nodeData} />),
      string: wrapNode((nodeData) => <Nodes.StringNode {...nodeData} />),
      target: wrapNode((nodeData) => <TargetNode {...nodeData} />),
      wildcardV2: wrapNode((nodeData) => (
        <Nodes.ModelBaseNode
          {...nodeData}
          updateNodeData={updateNodeData}
          editable={true}
          modelPricesMaps={modelPricesMaps}
        />
      )),
      workflow_output: wrapNode((nodeData) => <Nodes.OutputNode {...nodeData} />),
      point_cloud_from_depth: wrapNode((nodeData) => <Nodes.PointCloudFromDepthNode {...nodeData} />),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateNodeData],
  );

  const edgeTypes = React.useMemo(
    () => ({
      custom: (edgeData) => <CustomEdge {...edgeData} getDragState={getDragState} />,
    }),
    [getDragState],
  );

  useEffect(() => {
    const handleBackButton = async () => {
      if (role === 'editor') await saveRecipe();
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [saveRecipe, role]);

  const handleGoToDashboard = useCallback(() => {
    const path = recipe?.folderId ? `/folders/${recipe.folderId}` : '/';
    // Pass the target section so the Dashboard shows the correct scope
    // (especially for root-level workspace recipes that have no folderId).
    const dashboardSection = getDashboardSectionFromScope(recipe?.scope);
    navigate(path, { state: { dashboardSection } });

    const f = async () => {
      // todo: fix poster creation here
      // let posterImageUrl = recipe.poster;
      // if (!posterImageUrl) {
      //   posterImageUrl = await createPoster({ getNodes });
      // }
      void saveRecipe();
      resetWorkflowState();
    };
    void f();
  }, [recipe?.folderId, recipe?.scope, saveRecipe, navigate, resetWorkflowState]);

  const createPromptNode = useCallback(() => {
    const newPromptTemplate = {
      id: PROMPT_NODE_ID,
      displayName: 'Prompt',
    };
    addNewNode({ action: newPromptTemplate });
  }, [addNewNode]);

  /// KEYBOARD SHORTCUTS ACTIONS
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        isEditorOpen ||
        isGalleryOpen ||
        flowViewingMode !== FlowMode.Workflow ||
        !(event instanceof KeyboardEvent)
      ) {
        return; // Skip handling the event if it's on an input or textarea or in editing mode or not a keyboard event
      }

      const isCtrl = event.metaKey || event.ctrlKey;
      const isShift = event.shiftKey;

      if (isCtrl && event.key === 'z') {
        event.preventDefault();
        if (event.getModifierState('Shift')) {
          flowView.redo();
        } else {
          flowView.undo();
        }
      }

      if (isCtrl && event.key === 'd') {
        event.preventDefault();
        duplicateNodes();
      }
      if (isCtrl && event.key === 'c') {
        if (!window.getSelection().toString()?.length) {
          // copy node only if no text selection
          event.preventDefault();
          copyNodesToClipboard();
        }
      }
      // if (isCtrl && event.key === 'v') {
      //   event.preventDefault();
      //   pasteNodesFromClipboard(event);
      // }
      if (isCtrl && event.key === 's') {
        event.preventDefault();
        saveRecipe();
      }
      if (isCtrl && event.key === 'p') {
        event.preventDefault();
        createPromptNode();
      }
      if (isCtrl && (event.key === '+' || event.key === '=')) {
        event.preventDefault();
        reactFlowInstanceRef.current?.zoomIn();
      }
      if (isCtrl && event.key === '-') {
        event.preventDefault();
        reactFlowInstanceRef.current?.zoomOut();
      }
      if (isCtrl && !isShift && event.key === '0') {
        event.preventDefault();
        reactFlowInstanceRef.current?.zoomTo(1);
      }
      if (isCtrl && !isShift && event.key === '1') {
        event.preventDefault();
        fitView();
      }

      // Checked here instead of the early-return guard so that Ctrl shortcuts (undo, copy, etc.) still work
      // inside contentEditable elements, while plain V/H typing on prompt/text nodes won't trigger select/move
      const isUnmodified = !isCtrl && !isShift && !event.altKey;
      const isTargetEditable = event.target.isContentEditable;
      if (event.key.toLowerCase() === 'v' && isUnmodified && !isTargetEditable) {
        setIsSelectMode(true);
      }
      if (event.key.toLowerCase() === 'h' && isUnmodified && !isTargetEditable) {
        setIsSelectMode(false);
      }

      // '/' and Cmd/Ctrl+K shortcut for FloatMenu (only for editors)
      const isKShortcut = (event.key === 'k' || event.key === 'K') && isCtrl && !event.altKey && !isShift;
      const isSlashShortcut = event.key === '/' && !isCtrl && !event.altKey && !isShift && !isTargetEditable;
      const isTabShortcut = event.key === 'Tab' && !isCtrl && !event.altKey && !isShift;

      // Skip number keys (0-9) as they're handled by useKeyNavigation
      const isNumberKey = event.key >= '0' && event.key <= '9' && !isCtrl && !event.altKey && !isShift;

      if (role === 'editor' && !isFloatMenuOpen && (isSlashShortcut || isKShortcut || isTabShortcut) && !isNumberKey) {
        event.preventDefault();

        if (isKShortcut || !mousePosition.current) {
          // Cmd/Ctrl+K or no valid mouse position: always center, skip node detection
          const width = window.innerWidth;
          const height = window.innerHeight;
          openFloatMenu(Math.round(width / 2 - 100), Math.round(height / 2 - 100), true);
        } else {
          // Tab and '/': check for CustomGroup nodes at mouse position
          openFloatMenu(mousePosition.current.x, mousePosition.current.y);
        }
      }
      // if (event.key === 'Escape') {
      //   event.preventDefault();
      //   setShowGallery(false);
      // }
    };

    const handlePaste = (event) => {
      // If user is typing in an input/textarea/contenteditable, or in editor/gallery mode, or not in workflow mode, use default browser paste
      const activeElement = document.activeElement;
      if (
        (activeElement &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.isContentEditable)) ||
        isEditorOpen ||
        isGalleryOpen ||
        flowViewingMode !== FlowMode.Workflow
      ) {
        return;
      }
      // event.preventDefault();
      pasteNodesOrImageFromClipboard(event);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('paste', handlePaste);
    };
  }, [
    selectedNodes,
    nodes,
    duplicateNodes,
    copyNodesToClipboard,
    saveRecipe,
    createPromptNode,
    pasteNodesOrImageFromClipboard,
    isEditorOpen,
    isGalleryOpen,
    fitView,
    role,
    isFloatMenuOpen,
    flowView,
    flowViewingMode,
    mousePosition,
    openFloatMenu,
    setIsSelectMode,
  ]);

  const moveNodes = useCallback(
    (evt) => {
      if (isFloatMenuOpen) {
        return;
      }

      let delta = { x: 0, y: 0 };
      if (evt.key === 'ArrowUp') {
        delta.y = -1;
      } else if (evt.key === 'ArrowDown') {
        delta.y = 1;
      } else if (evt.key === 'ArrowLeft') {
        delta.x = -1;
      } else if (evt.key === 'ArrowRight') {
        delta.x = 1;
      }
      const scale = evt.shiftKey ? 100 : 10;
      delta.x *= scale;
      delta.y *= scale;

      flowView.moveNodesBy(flowView.selectedNodes, delta);
    },
    [isFloatMenuOpen, flowView],
  );

  const createGroupFromSelectedNodes = useCallback(
    (event) => {
      event.preventDefault();
      groupsManagerView.createGroupFromSelectedNodes();
    },
    [groupsManagerView],
  );

  // FIXME useHotkeys scope?
  useHotkeys(
    ['Delete', 'Backspace'],
    () => {
      if (
        isSomeNodesInRunningBatch(
          selectedNodes.map((node) => node.id),
          batches,
        )
      ) {
        return;
      }
      deleteSelected();
    },
    {
      enabled: isRecipeInteractionAllowed(isEditorOpen, isGalleryOpen, role, flowViewingMode, isNodeMenuOpened),
    },
  );
  useHotkeys(['up', 'down', 'left', 'right', 'shift+up', 'shift+down', 'shift+left', 'shift+right'], moveNodes, {
    enabled: isRecipeInteractionAllowed(isEditorOpen, isGalleryOpen, role, flowViewingMode, isNodeMenuOpened),
  });

  useHotkeys(getShortcut('GROUP_SELECTION'), createGroupFromSelectedNodes, {
    enabled:
      isNodeGroupingEnabled &&
      isRecipeInteractionAllowed(isEditorOpen, isGalleryOpen, role, flowViewingMode, isNodeMenuOpened),
  });

  useHotkeyScope('workflow');
  const { runNextConnectedNodes } = useFlowRunContext();
  useKeyNavigation(flowViewingMode, isNodeMenuOpened);

  const runNextConnectedNodesShortcut = useCallback(() => {
    try {
      runNextConnectedNodes();
    } catch (error) {
      logger.error('Could not run next connected nodes', error);
    }
  }, [runNextConnectedNodes]);

  useHotkeys(getShortcut('RUN_MODEL'), runNextConnectedNodesShortcut, {
    scopes: 'workflow',
    enableOnContentEditable: true,
    enableOnFormTags: true,
    enabled: isRecipeInteractionAllowed(isEditorOpen, isGalleryOpen, role, flowViewingMode, isNodeMenuOpened),
  });

  const handleUndoRedo = useCallback(
    (undoRedo) => {
      if (undoRedo === 'undo') {
        flowView.undo();
      } else {
        flowView.redo();
      }
    },
    [flowView],
  );

  //// END OF RUNNING MODELS

  //// DRAG EVENTS
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      // If the event was already handled by a child component, don't process it
      if (event.defaultPrevented) {
        return;
      }

      if (role !== 'editor') return;
      event.preventDefault();

      const itemString = event.dataTransfer.getData('menuItem');
      if (itemString) {
        let item;
        try {
          item = JSON.parse(itemString);
        } catch (_e) {
          return;
        }

        // check if the dropped element is valid
        if (typeof item === 'undefined' || !item) {
          return;
        }

        // handle drop on menu (probably user wants to cancel)
        if (item && event.clientX >= 240) {
          addNewNode({ action: item, dropX: event.clientX, dropY: event.clientY });
        }

        return;
      }
      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          // Check if file type is accepted
          const isAccepted = isFileTypeAccepted(file);

          if (!isAccepted) {
            logger.warn(`File type not supported: ${file.name} (${file.type})`);
            continue; // Skip this file
          }

          const item = {
            type: 'import',
            pastedData: { imageFile: file },
            id: 'wkKkBSd0yrZGwbStnU6rClone',
          };
          addNewNode({ action: item, dropX: event.clientX + i * 100, dropY: event.clientY + i * 100 });
        }
      }
    },
    [role, addNewNode],
  );

  const handleDuplicateRecipe = useCallback(async () => {
    try {
      const data = await duplicateRecipe(recipeId);
      goToRecipe(data.id, true);
    } catch (error) {
      logger.error('Could not duplicate recipe', error);
    }
  }, [duplicateRecipe, recipeId]);

  useEffect(() => {
    if (flowViewingMode === FlowMode.App) {
      setNotificationData({ isOpen: false });
    } else if (flowViewingMode === FlowMode.Workflow && (viewingVersionMode || role !== 'editor')) {
      let text, actionCtaText, onClick;
      if (!user) {
        text = t(I18N_KEYS.RECIPE_MAIN.FLOW.GENERAL.READ_ONLY_PANEL.TITLE_READER);
        actionCtaText = t(I18N_KEYS.RECIPE_MAIN.FLOW.GENERAL.READ_ONLY_PANEL.CTA_GUEST);
        onClick = () => navigate('/signin', { state: { from: window.location.pathname } });
      } else if (viewingVersionMode) {
        text = t(I18N_KEYS.RECIPE_MAIN.FLOW.GENERAL.READ_ONLY_PANEL.TITLE_EDITOR);
        actionCtaText = t(I18N_KEYS.RECIPE_MAIN.FLOW.GENERAL.READ_ONLY_PANEL.CTA_EDITOR);
        onClick = () => (window.location.href = window.location.pathname);
      } else {
        text = t(I18N_KEYS.RECIPE_MAIN.FLOW.GENERAL.READ_ONLY_PANEL.TITLE_READER);
        actionCtaText = t(I18N_KEYS.RECIPE_MAIN.FLOW.GENERAL.READ_ONLY_PANEL.CTA_READER);
        onClick = handleDuplicateRecipe;
      }

      setNotificationData({
        text,
        isOpen: true,
        icon: viewingVersionMode ? <WarningCircleIcon width={20} height={20} /> : <EyeIcon width={20} height={20} />,
        action: {
          text: actionCtaText,
          onClick,
        },
      });
    } else if (flowViewingMode === FlowMode.Workflow && role === 'editor' && notificationData.isOpen) {
      setNotificationData({
        isOpen: false,
      });
    }
  }, [flowViewingMode, viewingVersionMode, handleDuplicateRecipe, t, role, user, navigate, notificationData.isOpen]);

  const {
    onConnect: onReactFlowConnect,
    onEdgeUpdateStart,
    onEdgeUpdateEnd,
    onEdgeUpdate,
    onEdgesChange,
    onNodesChange,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onSelectionDragStart,
    onSelectionDrag,
    onSelectionDragStop,
    onSelectionStart,
    onSelectionEnd,
    isValidConnection,
  } = useReactFlowProps();

  const handleContextMenu = useCallback(
    (event) => {
      if (userPrefShowFloatMenuOnRightClick) {
        handleFloatMenu(event);
      }
    },
    [handleFloatMenu, userPrefShowFloatMenuOnRightClick],
  );

  // Proximity connect hook for shift+drag connections (feature flagged)
  useProximityConnect();

  // Combined onConnect handler that calls both the original and suggestion menu handlers
  const onConnect = useCallback(
    (connection) => {
      onReactFlowConnect(connection);
      onSuggestionConnect(connection);
    },
    [onReactFlowConnect, onSuggestionConnect],
  );

  // Combined onEdgeUpdate handler that calls both the original and suggestion menu handlers
  const onEdgeUpdateCombined = useCallback(
    (oldEdge, newConnection) => {
      onEdgeUpdate(oldEdge, newConnection);
      onSuggestionConnect(newConnection);
    },
    [onEdgeUpdate, onSuggestionConnect],
  );

  const onEdgeUpdateStartCombined = useCallback(
    (event, edge, handleType) => {
      onEdgeUpdateStart(event, edge, handleType);
      onSuggestionEdgeUpdateStart(event, edge, handleType);
    },
    [onEdgeUpdateStart, onSuggestionEdgeUpdateStart],
  );

  // Ref to track user-triggered menu selection
  const lastMenuChangeByUser = useRef(false);
  // Ref to prevent scroll jump after scroll-triggered menu change
  const justScrolledRef = useRef(false);
  // User-triggered setter
  const setSelectedMenuUser = useCallback((item) => {
    lastMenuChangeByUser.current = true;
    setSelectedMenu(item);
  }, []);

  const highlightedNodeIds = useWorkflowStore((state) => state.highlightedNodeIds);
  const suppressedSelectionNode = useWorkflowStore((state) => state.suppressedSelectionNode);
  const reactFlowNodes = useMemo(
    () =>
      nodes.map((node) =>
        toReactFlowNode(
          node,
          highlightedNodeIds,
          (nodeId) => flowView.getEffectiveIsLocked(nodeId),
          suppressedSelectionNode?.nodeId ?? null,
        ),
      ),
    [nodes, highlightedNodeIds, flowView, suppressedSelectionNode],
  );

  return (
    <>
      <AnimatePresence onExitComplete={clearEditor}>
        {isEditorOpen && (
          <motion.div
            style={{
              backgroundColor: color.Black64_T,
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: ZIndex.Panels.RECIPE_OVERLAY_PANELS,
              height: '100%',
              width: '100%',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <Editor />
          </motion.div>
        )}
      </AnimatePresence>

      <Box id="react-flow-container" sx={{ width: '100%', height: '100%' }} ref={flowRef}>
        {!isEditorOpen && (
          <ReactFlow
            defaultViewport={viewport ?? { x: 0, y: 0, zoom: DEFAULT_ZOOM_LEVEL }}
            edgeTypes={edgeTypes}
            edges={edges}
            fitView={!viewport && nodes.length > 1 && !activeTour}
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            minZoom={MIN_ZOOM_LEVEL}
            maxZoom={MAX_ZOOM_LEVEL}
            multiSelectionKeyCode={null}
            nodeDragThreshold={1}
            nodeTypes={staticNodeTypesComponent}
            nodes={reactFlowNodes}
            onConnect={onConnect}
            onConnectStart={onSuggestionConnectStart}
            onConnectEnd={onSuggestionConnectEnd}
            onContextMenu={handleContextMenu}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onEdgeUpdateStart={onEdgeUpdateStartCombined}
            onEdgeUpdate={onEdgeUpdateCombined}
            onEdgeUpdateEnd={onEdgeUpdateEnd}
            onInit={onInit}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStart={onNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            onNodeContextMenu={handleNodeContextMenu}
            onSelectionContextMenu={handleSelectionContextMenu}
            onSelectionDragStart={onSelectionDragStart}
            onSelectionDrag={onSelectionDrag}
            onSelectionDragStop={onSelectionDragStop}
            onSelectionStart={onSelectionStart}
            onSelectionEnd={onSelectionEnd}
            panOnDrag={isSelectMode ? [1, 2] : [0, 1]}
            panOnScroll={userPrefOnScroll !== undefined ? userPrefOnScroll : true}
            panOnScrollSpeed={1.5}
            selectionMode="partial"
            selectionOnDrag={isSelectMode}
            snapGrid={userPrefSnapToGrid !== false ? [60, 60] : [10, 10]}
            snapToGrid
            style={{ background: color.Black100 }}
            zoomOnDoubleClick={false}
            connectionRadius={70}
            connectionLineType={
              userPrefEdgeType === 'step' ? 'smoothstep' : userPrefEdgeType === 'line' ? 'straight' : 'bezier'
            }
            connectionLineComponent={CustomTempEdge}
            isValidConnection={isValidConnection}
            /// PERMISSIONS
            nodesDraggable={role !== 'guest'}
            nodesConnectable={role !== 'guest'}
            edgesUpdatable={role !== 'guest'}
            deleteKeyCode={null}
            disableKeyboardA11y={true}
            proOptions={{ hideAttribution: true }}
          >
            <Background color={color.Yambo_Black_Stroke} variant="dots" id="react-flow-background" />
            <Panel position="top-left" style={{ left: role === 'guest' ? 0 : TOOLBAR_WIDTH_V2 }}>
              <WorkflowDesignAppSwitch
                isEditingName={isEditingName}
                setIsEditingName={setIsEditingName}
                flowViewingMode={flowViewingMode}
                setFlowViewingMode={setFlowViewingMode}
                goToDashboard={handleGoToDashboard}
                shouldShowDesignAppToggle={shouldShowDesignAppToggle}
              />
            </Panel>
            <Panel position="top-right" style={{ right: 0, top: 100 }}>
              <ShortcutsPanel />
            </Panel>
            {shouldShowDesignAppToggle && (
              <Panel position="top-center">
                <AppPaper sx={{ height: '40px', alignItems: 'center', display: 'flex', px: 1 }}>
                  <AppToggleButtons
                    value={flowViewingMode}
                    btnW={60}
                    options={[
                      { value: FlowMode.Workflow, label: t(I18N_KEYS.RECIPE_MAIN.FLOW.WORKFLOW) },
                      { value: FlowMode.App, label: t(I18N_KEYS.RECIPE_MAIN.FLOW.DESIGN_APP) },
                    ]}
                    onChange={setFlowViewingMode}
                  />
                </AppPaper>
              </Panel>
            )}
            <Panel position="top-center" style={{ top: 40 }}>
              {recipe.isDeleted && (
                <Typography
                  data-testid="wea-debug-deleted-workflow-indicator"
                  sx={{ color: 'red', fontSize: 16, fontWeight: 'bold' }}
                >
                  DELETED WORKFLOW
                </Typography>
              )}
            </Panel>
            {!viewingVersionMode && role === 'editor' && (
              <>
                <Panel position="top-right">
                  <CreditsInfo />
                </Panel>
                {isTaskManagerOpen && (
                  <Panel position="top-right" style={{ right: CREDITS_INFO_WIDTH + 8 }}>
                    <TaskManager />
                  </Panel>
                )}
              </>
            )}

            <Panel position="bottom-center" style={{ margin: 0, marginBottom: '16px' }}>
              <Flex sx={{ gap: 1 }}>
                <FlowActionsBar
                  zoomLimitsPercentage={{ min: 1, max: 300 }}
                  onZoomIn={() => reactFlowInstanceRef.current?.zoomIn()}
                  onZoomOut={() => reactFlowInstanceRef.current?.zoomOut()}
                  onZoomToHundred={() => reactFlowInstanceRef.current?.zoomTo(1)}
                  onZoomToFit={() => fitView()}
                  isSelectMode={isSelectMode}
                  setIsSelectMode={setIsSelectMode}
                  isEditMode={!viewingVersionMode && role === 'editor'}
                  onUndoRedo={handleUndoRedo}
                  canUndo={flowView.hasUndo()}
                  canRedo={flowView.hasRedo()}
                />
              </Flex>
            </Panel>
            {/* All panels for editor (not guest) */}
            {!isDesignApp && role === 'editor' && (
              <>
                <Panel
                  data-testid="left-panel-panel"
                  position="left"
                  style={{
                    height: '100%',
                    width: `${PANEL_WIDTH}px`,
                    top: '0px',
                    left: `${TOOLBAR_WIDTH_V2}px`,
                    margin: 0,
                    opacity: selectedMenu ? 1 : 0,
                    transition: 'width 0.1s, opacity 0.2s ease-in-out',
                  }}
                  className={!isEditorOpen && selectedMenu ? 'slide-left-enter' : 'slide-left-exit'}
                >
                  <LeftPanelV2
                    selectedMenu={selectedMenu}
                    setSelectedItem={setSelectedMenuUser}
                    lastMenuChangeByUser={lastMenuChangeByUser}
                    justScrolledRef={justScrolledRef}
                    shouldHidePanel={isEditorOpen}
                  />
                </Panel>

                <Panel
                  data-testid="left_tool_menu"
                  position="left"
                  style={{
                    height: '100%',
                    width: `${TOOLBAR_WIDTH_V2}px`,
                    left: '0px',
                    top: '0px',
                    margin: 0,
                  }}
                >
                  <LeftToolMenuV2
                    selectedItem={selectedMenu}
                    setSelectedItem={setSelectedMenuUser}
                    goToDashboard={handleGoToDashboard}
                    onDuplicateRecipe={handleDuplicateRecipe}
                    panOnScrollEnabled={userPrefOnScroll}
                    onPanOnScrollChange={handlePanOnScrollChange}
                    showFloatMenuOnRightClickEnabled={userPrefShowFloatMenuOnRightClick}
                    onShowFloatMenuOnRightClickChange={handleShowFloatMenuOnRightClickChange}
                    requireAltKeyForSuggestionsEnabled={userPrefRequireAltKeyForSuggestions}
                    onRequireAltKeyForSuggestionsChange={handleRequireAltKeyForSuggestionsChange}
                    snapToGridEnabled={userPrefSnapToGrid}
                    onSnapToGridChange={handleSnapToGridChange}
                    edgeType={userPrefEdgeType}
                    onEdgeTypeChange={handleEdgeTypeChange}
                    onShareWorkflow={() => setIsShareModalOpen(true)}
                  />
                </Panel>

                {role === 'editor' && <Panel>{menuComponents.map((component) => component)}</Panel>}
                <PropertiesDrawerV2 selectedNodes={nodesForPropertiesDrawer} />
              </>
            )}
            {isGalleryOpen && (
              <Panel
                data-testid="media-gallery-panel"
                style={{
                  width: '100%',
                  height: '100%',
                  background: `${color.Black92_T}`,
                  top: 0,
                  margin: 0,
                  zIndex: ZIndex.Panels.RECIPE_OVERLAY_PANELS,
                }}
                className="fade-in"
              >
                <ModelMediaGallery />
              </Panel>
            )}
            {deleteGroupDialogData && (
              <Panel
                id="dialog-panel"
                style={{
                  width: '100%',
                  height: '100%',
                  top: 0,
                  margin: 0,
                  zIndex: ZIndex.Panels.RECIPE_OVERLAY_PANELS,
                }}
                className="fade-in"
              >
                <DeleteGroupDialog
                  groupNodeIds={deleteGroupDialogData.allNodeIdsToDelete.filter((id) => flowView.isGroupNode(id))}
                  onConfirm={handleDeleteGroupConfirm}
                  closeOverlayDialog={closeOverlayDialog}
                />
              </Panel>
            )}
            {flowViewingMode === FlowMode.App && (
              <Panel
                id="design-app-panel"
                style={{
                  width: '100%',
                  height: `calc(100% - ${DESIGN_APP_TOP_HEIGHT}px)`,
                  background: `${color.Black64_T}`,
                  backdropFilter: `blur(${BLUR_AMOUNT}px)`,
                  WebkitBackdropFilter: `blur(${BLUR_AMOUNT}px)`,
                  top: `${DESIGN_APP_TOP_HEIGHT}px`,
                  margin: 0,
                  zIndex: ZIndex.Panels.RECIPE_OVERLAY_PANELS,
                }}
                className="fade-in"
              >
                <DesignApp
                  isGuest={!user}
                  recipeId={recipeId}
                  nodes={nodes}
                  edges={edges}
                  readOnly={viewingVersionMode}
                  updateNodeData={updateNodeData}
                />
              </Panel>
            )}
            <Panel position="top-center" style={shouldShowDesignAppToggle ? { top: `${NAVBAR_HEIGHT}px` } : {}}>
              {notificationData.isOpen && (
                <NotificationsPanel
                  width={viewingVersionMode ? READ_ONLY_PANEL_EDITOR_WIDTH : READ_ONLY_PANEL_GUEST_WIDTH}
                  height={READ_ONLY_PANEL_HEIGHT}
                  content={notificationData}
                />
              )}
            </Panel>
            {/* end of panels for editor (not guest) */}
            <SuggestionMenuTooltip isOpen={suggestionMenuIsTooltipOpen} anchorEl={suggestionMenuAnchorEl} />
          </ReactFlow>
        )}
        <SaveErrorSnackbar />
      </Box>
      <ShareWorkflowModal open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} />
      <FlowTour />
    </>
  );
}

export default Flow;
